import {
  useEffect,
  useCallback,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  useSession,
  useDescope,
  isRefreshTokenExpired,
  isSessionTokenExpired,
} from '@descope/react-sdk';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  assignLocationAfterAuthFailure,
  clearLastVisitedPath,
  setLastVisitedPath,
} from '@/lib/lastVisitedPath';
import {
  registerTokenRefresh,
  unregisterTokenRefresh,
} from '@/lib/tokenRefreshBridge';
import { TokenManagerContext } from './TokenManagerContext';

export interface TokenManagerProviderProps {
  children: ReactNode;
}

export function TokenManagerProvider({
  children,
}: TokenManagerProviderProps): ReactNode {
  const { isAuthenticated, isSessionLoading, sessionToken } = useSession();
  const sdk = useDescope();
  const location = useLocation();
  const refreshInProgressRef = useRef(false);
  const sessionTeardownInProgressRef = useRef(false);
  const initialCheckDoneRef = useRef(false);
  const [isInitialCheckComplete, setIsInitialCheckComplete] = useState(false);

  const checkAndRefreshToken = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || isSessionLoading) {
      return;
    }

    /**
     * - Session JWT expiry → `sdk.refresh()` below when `isSessionTokenExpired`.
     * - Refresh token expiry → cannot refresh → same as refresh-failure: `logout` + full
     *   navigation via `assignLocationAfterAuthFailure` (avoid stuck authenticated UI if
     *   SDK state lags or `logout()` rejects).
     * - `clearLastVisitedPath` before assign: shared-tab hygiene so the next user does not
     *   inherit a return URL. `resolveRedirectPathAfterAuthFailure` then uses the current
     *   `window.location` when storage is empty — not a broken redirect. (Contrast: the
     *   axios 401 interceptor must not clear first; see `api-client.ts`.)
     */
    if (isRefreshTokenExpired()) {
      if (sessionTeardownInProgressRef.current) {
        return;
      }
      sessionTeardownInProgressRef.current = true;
      try {
        clearLastVisitedPath();
        try {
          await sdk.logout();
          assignLocationAfterAuthFailure();
        } catch (err: unknown) {
          console.warn(
            'Descope logout after refresh-token expiry failed:',
            err
          );
          assignLocationAfterAuthFailure();
        }
      } finally {
        sessionTeardownInProgressRef.current = false;
      }
      return;
    }

    if (refreshInProgressRef.current) {
      return;
    }

    let tokenToCheck = sessionToken;

    try {
      const stored = localStorage.getItem('DS');
      if (stored) {
        if (stored.startsWith('{')) {
          const session = JSON.parse(stored);
          tokenToCheck = session.sessionToken || sessionToken;
        } else {
          tokenToCheck = stored;
        }
      }
    } catch {
      // Silently fall back to sessionToken from hook
    }

    if (!tokenToCheck || !isSessionTokenExpired(tokenToCheck)) {
      return;
    }

    refreshInProgressRef.current = true;

    try {
      await sdk.refresh();

      Sentry.addBreadcrumb({
        message: 'Token refreshed successfully',
        level: 'info',
        category: 'auth',
      });
    } catch (error) {
      console.error('Token refresh failed:', error);

      Sentry.captureException(error, {
        tags: {
          error_type: 'token_refresh_failed',
          context: 'TokenManager',
        },
        extra: {
          message: 'Token refresh failed, logging out user',
        },
        level: 'error',
      });

      // Same clear-before-assign as refresh-token expiry (see comment above).
      if (sessionTeardownInProgressRef.current) {
        return;
      }
      sessionTeardownInProgressRef.current = true;
      try {
        clearLastVisitedPath();
        try {
          await sdk.logout();
          assignLocationAfterAuthFailure();
        } catch (logoutError) {
          console.error('Logout failed:', logoutError);
          assignLocationAfterAuthFailure();
        }
      } finally {
        sessionTeardownInProgressRef.current = false;
      }
    } finally {
      refreshInProgressRef.current = false;
    }
  }, [isAuthenticated, isSessionLoading, sessionToken, sdk]);

  // Persist latest path while authenticated (used after 401 to restore URL post-login)
  useEffect(() => {
    if (!isAuthenticated || isSessionLoading) {
      return;
    }
    setLastVisitedPath(`${location.pathname}${location.search}`);
  }, [isAuthenticated, isSessionLoading, location.pathname, location.search]);

  // Wire sdk.refresh() into the Axios 401 retry interceptor via the bridge
  useEffect(() => {
    registerTokenRefresh(async (): Promise<void> => {
      await sdk.refresh();
    });
    return () => {
      unregisterTokenRefresh();
    };
  }, [sdk]);

  // Reset initial check flag when user logs out; drop last-visited path for next user in this tab
  useEffect(() => {
    if (!isAuthenticated && !isSessionLoading) {
      clearLastVisitedPath();
      initialCheckDoneRef.current = false;
      setIsInitialCheckComplete(false);
    }
  }, [isAuthenticated, isSessionLoading]);

  // Initial token check on mount (blocks rendering until complete)
  useEffect(() => {
    if (isSessionLoading) {
      return;
    }

    if (!isAuthenticated) {
      setIsInitialCheckComplete(true);
      return;
    }

    if (initialCheckDoneRef.current) {
      return;
    }

    initialCheckDoneRef.current = true;

    const performInitialCheck = async (): Promise<void> => {
      try {
        await checkAndRefreshToken();
      } catch (error) {
        console.error('Initial token check failed:', error);
        Sentry.captureException(error, {
          tags: {
            error_type: 'initial_token_check_failed',
            context: 'TokenManager',
          },
          extra: {
            message: 'Initial token check failed, allowing app to render',
          },
          level: 'error',
        });
      } finally {
        setIsInitialCheckComplete(true);
      }
    };

    performInitialCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSessionLoading]);

  // Check token on window focus (after initial check)
  useEffect(() => {
    if (!isAuthenticated || isSessionLoading || !isInitialCheckComplete) {
      return;
    }

    const handleFocus = (): void => {
      checkAndRefreshToken();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [
    isAuthenticated,
    isSessionLoading,
    isInitialCheckComplete,
    checkAndRefreshToken,
  ]);

  // DSR/DSS check when tab becomes visible (after initial check). Complements `focus`:
  useEffect(() => {
    if (!isAuthenticated || isSessionLoading || !isInitialCheckComplete) {
      return;
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        void checkAndRefreshToken();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    isAuthenticated,
    isSessionLoading,
    isInitialCheckComplete,
    checkAndRefreshToken,
  ]);

  // Check token on route changes (after initial check)
  useEffect(() => {
    if (!isAuthenticated || isSessionLoading || !isInitialCheckComplete) {
      return;
    }

    checkAndRefreshToken();
  }, [
    location.pathname,
    isAuthenticated,
    isSessionLoading,
    isInitialCheckComplete,
    checkAndRefreshToken,
  ]);

  // Block rendering until initial token check completes
  if ((isSessionLoading || isAuthenticated) && !isInitialCheckComplete) {
    return (
      <div className="min-h-screen grid place-items-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <TokenManagerContext.Provider value={{ checkAndRefreshToken }}>
      {children}
    </TokenManagerContext.Provider>
  );
}
